import { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { ProjectMilestoneStatus, type ProjectMilestone } from '../../graphql/types';
import './MilestoneTimeline.css';

export interface MilestoneTimelineProps {
  milestones: ProjectMilestone[];
  projectStartDate?: string;
  projectEndDate?: string;
}

const WIDTH = 600;
const HEIGHT = 160;
const MARGIN = { top: 20, right: 20, bottom: 40, left: 120 };
const BAR_HEIGHT = 18;
const BAR_GAP = 10;

const STATUS_COLOR: Record<string, string> = {
  [ProjectMilestoneStatus.Complete]: '#2ca02c',
  [ProjectMilestoneStatus.InProgress]: '#1f77b4',
  [ProjectMilestoneStatus.Slipped]: '#d62728',
  [ProjectMilestoneStatus.NotStarted]: '#9e9e9e',
};

function parseDate(s: string | undefined | null): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

export function MilestoneTimeline({
  milestones,
  projectStartDate,
  projectEndDate,
}: MilestoneTimelineProps): JSX.Element {
  const svgRef = useRef<SVGSVGElement>(null);

  const sorted = [...milestones].sort((a, b) => a.sequence - b.sequence);

  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    if (sorted.length === 0) return;

    const innerW = WIDTH - MARGIN.left - MARGIN.right;
    const innerH = sorted.length * (BAR_HEIGHT + BAR_GAP);
    const totalH = innerH + MARGIN.top + MARGIN.bottom;

    svg.attr('height', totalH);

    const now = new Date();
    const dates = sorted.flatMap((m) => [parseDate(m.targetDate)]).filter(Boolean) as Date[];

    const domainStart =
      parseDate(projectStartDate) ??
      (dates.length ? d3.min(dates)! : new Date(now.getFullYear(), 0, 1));
    const domainEnd =
      parseDate(projectEndDate) ??
      (dates.length ? d3.max(dates)! : new Date(now.getFullYear(), 11, 31));

    const xScale = d3.scaleTime().domain([domainStart, domainEnd]).range([0, innerW]).clamp(true);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    // Axis
    const xAxis = d3
      .axisBottom(xScale)
      .ticks(5)
      .tickFormat(d3.timeFormat('%b %Y') as (d: Date | d3.NumberValue) => string);
    g.append('g')
      .attr('transform', `translate(0,${innerH})`)
      .call(xAxis)
      .attr('color', 'var(--forge-theme-text-medium, #757575)');

    // Today marker
    if (now >= domainStart && now <= domainEnd) {
      const todayX = xScale(now);
      g.append('line')
        .attr('x1', todayX)
        .attr('x2', todayX)
        .attr('y1', 0)
        .attr('y2', innerH)
        .attr('stroke', '#ff7f0e')
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '4,3');

      g.append('text')
        .attr('x', todayX + 3)
        .attr('y', 10)
        .attr('font-size', '9px')
        .attr('fill', '#ff7f0e')
        .text('today');
    }

    // Milestone bars
    sorted.forEach((ms, i) => {
      const y = i * (BAR_HEIGHT + BAR_GAP);
      const color = STATUS_COLOR[ms.status] ?? '#9e9e9e';
      const targetDate = parseDate(ms.targetDate);

      // Label
      const label = ms.name.length > 16 ? ms.name.slice(0, 15) + '…' : ms.name;
      g.append('text')
        .attr('x', -8)
        .attr('y', y + BAR_HEIGHT / 2)
        .attr('dy', '0.35em')
        .attr('text-anchor', 'end')
        .attr('font-size', '11px')
        .attr('fill', 'var(--forge-theme-text-primary, #212121)')
        .text(label);

      if (targetDate) {
        const barEnd = xScale(targetDate);
        const barStart = 0;
        const barW = Math.max(4, barEnd - barStart);

        g.append('rect')
          .attr('x', barStart)
          .attr('y', y)
          .attr('width', barW)
          .attr('height', BAR_HEIGHT)
          .attr('fill', color)
          .attr('rx', 3)
          .attr('opacity', 0.85);

        g.append('title').text(
          `${ms.name}: ${ms.status}${targetDate ? ` — ${d3.timeFormat('%b %d, %Y')(targetDate)}` : ''}`
        );
      } else {
        // No date: draw a small diamond marker at start
        g.append('path')
          .attr('d', `M0,${y + BAR_HEIGHT / 2} l6,-6 l6,6 l-6,6 Z`)
          .attr('fill', color)
          .attr('opacity', 0.85);
      }
    });
  }, [sorted, projectStartDate, projectEndDate]);

  const dynamicH =
    sorted.length > 0
      ? sorted.length * (BAR_HEIGHT + BAR_GAP) + MARGIN.top + MARGIN.bottom
      : HEIGHT;

  return (
    <div className="milestone-timeline">
      {sorted.length === 0 ? (
        <p className="milestone-timeline-empty forge-typography--body2">
          No milestones defined yet.
        </p>
      ) : (
        <svg
          ref={svgRef}
          width={WIDTH}
          height={dynamicH}
          role="img"
          aria-label="Milestone timeline Gantt chart showing project milestones by target date and status"
        />
      )}
    </div>
  );
}
